<script setup lang="ts">
import type { ModelRef } from "vue";
import { useAcl } from "vue-simple-acl";
import { useSideNavigation } from "~/core/composables/useSideNavigation";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import { Filters } from "~/core/models/filters";
import PkiListDeleteAction from "~/modules/pki/components/PkiListDeleteAction.vue";
import { useFetchPkis } from "~/modules/pki/composables/useFetchPkis";
import type { Pki } from "~/modules/pki/models/Pki.interface";
import { PATH_SUPER_ADMINISTRATION_SETTINGS_PKI } from "~/modules/superAdministration/constants";

const { t } = useI18n();
const { can } = useAcl();
const { navigateToPath, onClose } = useSideNavigation();

const {
  isLoading,
  error,
  results,
  fetchData,
  onSearch,
  filters,
  setFilter,
  search,
} = useFetchPkis();

const showPkiForm: ModelRef<boolean> = defineModel<boolean>("showPkiForm", {
  required: true,
});
const currentSelectedPki: ModelRef<string | undefined> = defineModel<
  string | undefined
>("currentSelectedPki");

const searchQuery = computed({
  get() {
    return (filters.get(Filters.PKI_ALIAS) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.PKI_ALIAS, searchQuery);
  },
});

const reloadWithContext = () => {
  fetchData(results.value?.pagination.page, results.value?.pagination.pageSize);
};

const onSelectPki = async (pki: Pki) => {
  if (can(ACLRoles.CAN_READ_FEATURE)) {
    await navigateToPath(
      `${PATH_SUPER_ADMINISTRATION_SETTINGS_PKI}/${pki.alias}`,
    );
  }
};

function onCloseSideCanvas(event: CloseEvent) {
  onClose(event, PATH_SUPER_ADMINISTRATION_SETTINGS_PKI, reloadWithContext);
}

onSearch(fetchData);
</script>

<template>
  <div class="w-3/5 mb-4">
    <search-bar
      v-model:search-query="searchQuery"
      :search-placeholder="t('pki.filters.searchByAlias')"
      @clear="search"
      @search="search"
    />
  </div>

  <lxc-table
    :is-loading="isLoading"
    :data="results?.results"
    :empty-text="t('pki.noPki')"
    :context="results?.pagination"
    :error="error?.toError()"
    :clickable="can(ACLRoles.CAN_READ_FEATURE)"
    @change-page-and-page-size="fetchData"
    @row-click="onSelectPki"
  >
    <lxc-table-column prop="alias" :label="t('pki.alias')" />
    <lxc-table-column prop="type" :label="t('pki.type.label')">
      <template #default="scope">{{
        t(`pki.type.${scope.row.type}`)
      }}</template>
    </lxc-table-column>
    <lxc-table-column prop="url" :label="t('pki.url')" />
    <lxc-table-column class="!py-0" width="15rem">
      <template #default="scope">
        <div class="flex items-center gap-2">
          <pki-list-test-connection-action :pki="scope.row" />
          <pki-list-delete-action
            :pki="scope.row"
            @change="reloadWithContext"
          />
        </div>
      </template>
    </lxc-table-column>
  </lxc-table>
  <pki-form
    v-model:show="showPkiForm"
    :selected-pki="currentSelectedPki"
    @close="onCloseSideCanvas"
  />
</template>

<style scoped lang="scss">
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
